/**
 * Kenyan mobile validation for insured / customer phones.
 * Rejects values that look like vehicle sum insured (common data-entry mistake).
 */

export function digitsOnly(value) {
  return String(value == null ? '' : value).replace(/\D/g, '');
}

/** True for Kenyan mobile 9-digit local form: Safaricom/Telkom 7… or Airtel 1… */
export function isKenyanMobileNineDigits(nine) {
  return Boolean(nine && nine.length === 9 && (nine.startsWith('7') || nine.startsWith('1')));
}

/** Normalize to 9-digit Kenyan mobile (7XXXXXXXX or 1XXXXXXXX). Returns null if not a mobile. */
export function normalizeKenyanMobileDigits(phone) {
  const digits = digitsOnly(phone);
  if (!digits) return null;

  let nine = null;
  if (digits.startsWith('254') && digits.length >= 12) {
    nine = digits.slice(-9);
  } else if (digits.startsWith('0') && digits.length >= 10) {
    nine = digits.slice(-9);
  } else if (digits.length === 9) {
    nine = digits;
  } else if (digits.length === 10 && digits.startsWith('0')) {
    nine = digits.slice(1);
  } else if (digits.length > 9) {
    const tail = digits.slice(-9);
    if (isKenyanMobileNineDigits(tail)) nine = tail;
  }

  if (!isKenyanMobileNineDigits(nine)) return null;
  return nine;
}

/** Storage/display format used across the portal (07XXXXXXXX or 01XXXXXXXX). */
export function formatKenyanMobileForStorage(phone) {
  const nine = normalizeKenyanMobileDigits(phone);
  if (!nine) return null;
  return `0${nine}`;
}

function parsePositiveKesAmount(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n));
}

function collectVehicleValueDigits(context) {
  if (!context || typeof context !== 'object') return [];
  const raw = [
    context.vehicleValue,
    context.sumInsured,
    context.sum_insured,
    context.vehicle_value
  ];
  const out = new Set();
  for (const v of raw) {
    const d = parsePositiveKesAmount(v);
    if (d) out.add(d);
  }
  return [...out];
}

function isTrailingZeroPadding(longer, shorter) {
  return (
    longer.length > shorter.length &&
    longer.startsWith(shorter) &&
    /^0+$/.test(longer.slice(shorter.length))
  );
}

/** True when phone digits match (or closely mirror) a vehicle value field. */
export function phoneMatchesVehicleValue(phone, context) {
  const phoneDigits = digitsOnly(phone);
  if (!phoneDigits) return false;
  const phoneTrim = phoneDigits.replace(/^0+/, '');
  if (!phoneTrim) return false;
  for (const v of collectVehicleValueDigits(context)) {
    if (phoneDigits === v || phoneTrim === v) return true;
    if (isTrailingZeroPadding(phoneTrim, v) || isTrailingZeroPadding(v, phoneTrim)) return true;
  }
  return false;
}

export function validateKenyanInsuredPhone(phone, context) {
  const raw = String(phone == null ? '' : phone).trim();
  if (!raw) {
    return {
      valid: false,
      message: 'Insured phone number is required. Use a Kenyan mobile number (07XXXXXXXX or 01XXXXXXXX).',
      storageFormat: null,
      normalizedNine: null
    };
  }

  const nine = normalizeKenyanMobileDigits(raw);
  if (nine) {
    return {
      valid: true,
      message: 'Valid Kenyan mobile number',
      storageFormat: `0${nine}`,
      normalizedNine: nine
    };
  }

  if (phoneMatchesVehicleValue(raw, context)) {
    return {
      valid: false,
      message:
        'Phone number cannot be the same as vehicle value. Enter the insured\'s M-Pesa mobile number (07XXXXXXXX or 01XXXXXXXX).',
      storageFormat: null,
      normalizedNine: null
    };
  }

  return {
    valid: false,
    message: 'Insured phone number is invalid. Use 07XXXXXXXX, 01XXXXXXXX, 7XXXXXXXX, 1XXXXXXXX, or +2547XXXXXXXX / +2541XXXXXXXX.',
    storageFormat: null,
    normalizedNine: null
  };
}

export function resolveInsuredPhoneForIssuance({ clientPhone, paymentPhone, vehicleValue, sumInsured, sum_insured }) {
  const context = { vehicleValue, sumInsured, sum_insured };
  const clientResult = validateKenyanInsuredPhone(clientPhone, context);
  if (clientResult.valid) {
    return { ...clientResult, source: 'client' };
  }
  const paymentResult = validateKenyanInsuredPhone(paymentPhone, context);
  if (paymentResult.valid) {
    return { ...paymentResult, source: 'payment' };
  }
  return {
    valid: false,
    message: clientResult.message || paymentResult.message,
    storageFormat: null,
    normalizedNine: null,
    source: null
  };
}

export function withInsuredPhoneFromMobile(insuredData, mobileNumber, vehicleData) {
  const base = insuredData && typeof insuredData === 'object' ? { ...insuredData } : {};
  const context = {
    vehicleValue: vehicleData?.vehicleValue,
    sumInsured: vehicleData?.sumInsured
  };
  const mobileResult = validateKenyanInsuredPhone(mobileNumber, context);
  if (mobileResult.valid) {
    base.phoneNumber = mobileResult.storageFormat;
    return base;
  }
  const insuredResult = validateKenyanInsuredPhone(base.phoneNumber, context);
  if (insuredResult.valid) {
    base.phoneNumber = insuredResult.storageFormat;
  }
  return base;
}

export function assertValidInsuredPhoneForSave(phone, context) {
  const result = validateKenyanInsuredPhone(phone, context);
  if (!result.valid) {
    const err = new Error(result.message);
    err.code = 'INVALID_INSURED_PHONE';
    throw err;
  }
  return result.storageFormat;
}

export async function applyResolvedInsuredPhoneForIssuanceDb(pool, applicationData, referenceNumber) {
  if (!applicationData || !applicationData.insuredData) return { applied: false };
  const vd = applicationData.vehicleDetails || applicationData.vehicleData || {};
  const ctx = {
    vehicleValue: vd.vehicleValue ?? vd.sumInsured,
    sumInsured: vd.sumInsured ?? vd.vehicleValue
  };
  let paymentPhone = null;
  if (referenceNumber && pool) {
    try {
      const payRow = await pool.query(
        'SELECT customer_phone FROM payment_statuses WHERE reference_number = $1 LIMIT 1',
        [referenceNumber]
      );
      paymentPhone = payRow.rows[0]?.customer_phone || null;
    } catch (_) {}
  }
  const resolved = resolveInsuredPhoneForIssuance({
    clientPhone: applicationData.insuredData.phoneNumber,
    paymentPhone,
    ...ctx
  });
  if (!resolved.valid) return { applied: false, message: resolved.message };
  applicationData.insuredData.phoneNumber = resolved.storageFormat;
  const clientId = applicationData.clientId;
  if (resolved.source === 'payment' && pool && clientId && typeof clientId === 'string' && clientId.trim()) {
    try {
      await pool.query(
        'UPDATE clients SET phone_number = $1, updated_at = NOW() WHERE client_id = $2',
        [resolved.storageFormat, clientId.trim()]
      );
    } catch (e) {
      console.warn('⚠️ applyResolvedInsuredPhoneForIssuanceDb: could not update client phone:', e.message);
    }
  }
  return { applied: true, source: resolved.source, phone: resolved.storageFormat };
}
